/**
 * Read a catalog definition as it stood at an instant.
 *
 * Server-orchestrated, so a tRPC query rather than a data-store collection —
 * the same shape as `usePatientTimeline`. Unlike that one it never needs
 * invalidating: a past instant is immutable, so once fetched it cannot go stale.
 *
 * KEYED BY AN INSTANT, NEVER A VERSION. Catalog edges are unpinned, so children
 * move underneath a parent that was never re-authored — a version therefore
 * names no single state of the graph. `useDefinitionChangePoints` gives the
 * instants where the answer actually differs, which is what the UI offers.
 *
 * Two round trips by design. The first is cheap and reports the graph's shape;
 * that shape decides which lineages are worth asking for content on, because a
 * composed kind's resources run to hundreds of KB and a leaf kind's to a few.
 */

import { type ChangePoint, getTRPCClient, type ResolveDefinitionAsOfResult } from "@repo/api";
import {
  type DefinitionRevisionChange,
  diffRevisionPair,
  includeForKind,
  type ProjectableKind,
  projectDefinitionAsOf,
} from "@repo/data-store";
import { useQuery } from "@tanstack/react-query";

export function definitionAsOfQueryKey(definitionUrl: string | undefined, asOf: string | undefined) {
  return ["catalog", "asOf", "resolve", definitionUrl, asOf] as const;
}

export interface DefinitionAsOf<T> {
  definition: T | undefined;
  /** The revision each reachable lineage resolved to, root included. */
  provenance: ResolveDefinitionAsOfResult["provenance"];
  /** Lineages that could not be placed at this instant. Never a thrown error. */
  unresolved: ResolveDefinitionAsOfResult["unresolved"];
  isLoading: boolean;
}

/**
 * `asOf` undefined means the caller wants the live read instead, so this stays
 * disabled rather than silently resolving "now" — two paths that both answer
 * would be two sources of truth for the present.
 */
export function useDefinitionAsOf<T>(definitionUrl: string | undefined, asOf: string | undefined): DefinitionAsOf<T> {
  const query = useQuery({
    queryKey: definitionAsOfQueryKey(definitionUrl, asOf),
    queryFn: async () => {
      if (!definitionUrl || !asOf) return undefined;
      const client = getTRPCClient();
      const shape = await client.catalog.asOf.resolve.query({ definitionUrl, asOf });
      const include = includeForKind(
        shape.tree.kind as ProjectableKind,
        definitionUrl,
        shape.provenance.map((entry) => entry.lineage),
      );
      const full = await client.catalog.asOf.resolve.query({ definitionUrl, asOf, include });
      return { ...full, definition: projectDefinitionAsOf(full) };
    },
    enabled: Boolean(definitionUrl && asOf),
    // A past instant cannot change. Refetching it only costs a round trip.
    staleTime: Number.POSITIVE_INFINITY,
  });

  return {
    definition: query.data?.definition as T | undefined,
    provenance: query.data?.provenance ?? [],
    unresolved: query.data?.unresolved ?? [],
    isLoading: query.isLoading,
  };
}

export interface DefinitionView<T> {
  /** Undefined means "not found", never "still arriving" — check `isPending` first. */
  definition: T | undefined;
  isPending: boolean;
  /** True while a past instant is being rendered. Gates the whole write surface. */
  isPast: boolean;
}

/**
 * The one branch every catalog detail route makes: live read, or the as-of read.
 *
 * The caller passes the live DTO in rather than this hook fetching it, because
 * each kind has its own live hook and they return different types. The caller's
 * side of the deal is to disable that hook when `asOf` is set — pass it
 * `undefined` for the url — so exactly one of the two ever fetches.
 */
export function useDefinitionView<T>(
  definitionUrl: string | undefined,
  asOf: string | undefined,
  live: T | undefined,
): DefinitionView<T> {
  const past = useDefinitionAsOf<T>(definitionUrl, asOf);
  if (!asOf) return { definition: live, isPending: false, isPast: false };
  return { definition: past.definition, isPending: past.isLoading, isPast: true };
}

/**
 * The instants at which this definition's graph differs, newest first, each
 * naming what was authored then.
 *
 * Distinct from the version history, and that difference is the whole point: a
 * revision of a CHILD is a change point without being a version of this
 * definition. On a composed kind most change points are of that sort, and
 * nothing else in the UI can reach them.
 */
export function useDefinitionChangePoints(definitionUrl: string | undefined): {
  changePoints: readonly ChangePoint[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["catalog", "asOf", "changePoints", definitionUrl] as const,
    queryFn: async () => {
      if (!definitionUrl) return [];
      const result = await getTRPCClient().catalog.asOf.changePoints.query({ definitionUrl });
      return result.changePoints;
    },
    enabled: Boolean(definitionUrl),
  });
  return { changePoints: query.data ?? [], isLoading: query.isLoading };
}

/**
 * What one revision changed, fetched only when a change-point row is opened.
 *
 * Lazy on purpose. A composed definition's graph has a change point per revision
 * of every lineage under it, and eagerly diffing all of them would cost a query
 * apiece for rows nobody expands. `enabled` is the row's open state.
 *
 * Immutable once fetched — a past revision and its predecessor cannot change —
 * so this never needs invalidating.
 */
export function useRevisionChanges(
  lineage: string | undefined,
  version: number | undefined,
  enabled: boolean,
): { changes: DefinitionRevisionChange[]; isLoading: boolean } {
  const query = useQuery({
    queryKey: ["catalog", "asOf", "revisionPair", lineage, version] as const,
    queryFn: async () => {
      if (!lineage || version === undefined) return [];
      const pair = await getTRPCClient().catalog.asOf.revisionPair.query({ lineage, version });
      return pair ? diffRevisionPair(pair) : [];
    },
    enabled: enabled && Boolean(lineage) && version !== undefined,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return { changes: query.data ?? [], isLoading: query.isLoading };
}
